import React, { useMemo, useState } from 'react';
import {
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
  ScrollView,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { goBackOrHome } from '../../navigation/goBackOrHome';
import { useAppSelector } from '../../store/store';
import { fpColors } from '../../styles/theme';
import { LentItemRow } from './components/LentItemRow';
import { selectLentOutItems } from '../browse/browseSlice';

/**
 * MyCustodyScreen — Sprint 8 Phase C.
 *
 * "Where are my things?" — items the user OWNS that someone else
 * is currently holding (via crew handoff). Derived from the
 * already-loaded inventory cache (no extra fetch).
 *
 * Filter chips:
 *   - All        → every item with a non-self custodian
 *   - Overdue    → custody_due_date < now
 *   - Due soon   → due in the next 24h
 *
 * Tap any row → ItemDetail (where Recall + Send reminder live).
 *
 * Reached via the Share-tab "Out on loan" section's [View all →].
 */

type FilterKey = 'all' | 'overdue' | 'due-soon';

export const MyCustodyScreen: React.FC = () => {
  const navigation = useNavigation<any>();
  const lentOut = useAppSelector(selectLentOutItems);

  const [filter, setFilter] = useState<FilterKey>('all');

  const counts = useMemo(() => {
    const now = Date.now();
    let overdue = 0;
    let dueSoon = 0;
    for (const item of lentOut) {
      const due = item.custody_due_date
        ? new Date(item.custody_due_date).getTime()
        : null;
      if (due == null || isNaN(due)) continue;
      if (due < now) overdue += 1;
      else if (due - now < 1000 * 60 * 60 * 24) dueSoon += 1;
    }
    return { overdue, dueSoon };
  }, [lentOut]);

  const visible = useMemo(() => {
    const now = Date.now();
    return lentOut.filter((item: any) => {
      const due = item.custody_due_date
        ? new Date(item.custody_due_date).getTime()
        : null;
      switch (filter) {
        case 'overdue':
          return due != null && !isNaN(due) && due < now;
        case 'due-soon':
          return (
            due != null &&
            !isNaN(due) &&
            due >= now &&
            due - now < 1000 * 60 * 60 * 24
          );
        case 'all':
        default:
          return true;
      }
    });
  }, [lentOut, filter]);

  const goItemDetail = (itemId: number) =>
    navigation.navigate('ItemDetail', { itemId });

  return (
    <View style={styles.page}>
      <View style={styles.topBar}>
        <Pressable
          onPress={() => goBackOrHome(navigation)}
          style={styles.backBtn}
          hitSlop={8}
          accessibilityRole="button"
          accessibilityLabel="Go back"
        >
          <MaterialIcons
            name="arrow-back"
            size={22}
            color={fpColors.onSurface}
          />
        </Pressable>
        <Text style={styles.title}>Out on loan</Text>
        <View style={{ width: 36 }} />
      </View>

      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.chipRow}
      >
        <FilterChip
          label={`All (${lentOut.length})`}
          active={filter === 'all'}
          onPress={() => setFilter('all')}
        />
        <FilterChip
          label={`Overdue (${counts.overdue})`}
          tone="error"
          active={filter === 'overdue'}
          onPress={() => setFilter('overdue')}
        />
        <FilterChip
          label={`Due soon (${counts.dueSoon})`}
          tone="warning"
          active={filter === 'due-soon'}
          onPress={() => setFilter('due-soon')}
        />
      </ScrollView>

      <ScrollView contentContainerStyle={styles.list}>
        {visible.length === 0 ? (
          <View style={styles.emptyState}>
            <MaterialIcons
              name="local-shipping"
              size={36}
              color={fpColors.outline}
            />
            <Text style={styles.emptyText}>
              {filter === 'overdue'
                ? 'Nothing is overdue. Nice.'
                : filter === 'due-soon'
                  ? 'Nothing is coming due in the next 24 hours.'
                  : "You haven't handed anything off."}
            </Text>
          </View>
        ) : (
          <View style={styles.card}>
            {visible.map((item: any) => (
              <LentItemRow
                key={item.id}
                itemId={item.id}
                itemName={item.item_name ?? 'Untitled item'}
                custodianUserId={item.current_custodian_user_id}
                custodySince={item.custody_since}
                custodyDueDate={item.custody_due_date}
                onPress={() => goItemDetail(item.id)}
              />
            ))}
          </View>
        )}
      </ScrollView>
    </View>
  );
};

const FilterChip: React.FC<{
  label: string;
  active: boolean;
  onPress: () => void;
  tone?: 'default' | 'error' | 'warning';
}> = ({ label, active, onPress, tone = 'default' }) => {
  const activeBg =
    tone === 'error'
      ? fpColors.errorContainer
      : tone === 'warning'
        ? fpColors.tertiaryContainer
        : fpColors.primaryContainer;
  const activeFg =
    tone === 'error'
      ? fpColors.error
      : tone === 'warning'
        ? fpColors.onTertiaryContainer
        : fpColors.onPrimaryContainer;

  return (
    <Pressable
      onPress={onPress}
      style={({ pressed }) => [
        styles.chip,
        active && { backgroundColor: activeBg, borderColor: activeFg },
        pressed && { opacity: 0.85 },
      ]}
      accessibilityRole="button"
      accessibilityState={{ selected: active }}
    >
      <Text
        style={[
          styles.chipLabel,
          active && { color: activeFg, fontFamily: 'Inter_700Bold' },
        ]}
      >
        {label}
      </Text>
    </Pressable>
  );
};

const styles = StyleSheet.create({
  page: { flex: 1, backgroundColor: fpColors.background },
  topBar: {
    height: Platform.OS === 'ios' ? 104 : 68,
    paddingTop: Platform.OS === 'ios' ? 48 : 12,
    paddingHorizontal: 12,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: fpColors.surfaceContainerLowest,
    borderBottomWidth: 1,
    borderBottomColor: fpColors.outlineVariant,
  },
  backBtn: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: fpColors.surfaceContainerLow,
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: {
    flex: 1,
    textAlign: 'center',
    fontFamily: 'Inter_700Bold',
    fontSize: 16,
    color: fpColors.onSurface,
  },
  chipRow: {
    paddingHorizontal: 14,
    paddingTop: 12,
    paddingBottom: 6,
    gap: 6,
  },
  chip: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 14,
    backgroundColor: fpColors.surfaceContainerLow,
    borderWidth: 1,
    borderColor: 'transparent',
  },
  chipLabel: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
  },
  list: { padding: 14, paddingTop: 6, gap: 14 },
  card: {
    backgroundColor: fpColors.surfaceContainerLowest,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    borderRadius: 14,
    overflow: 'hidden',
  },
  emptyState: {
    paddingVertical: 64,
    alignItems: 'center',
    gap: 12,
  },
  emptyText: {
    fontFamily: 'Inter_500Medium',
    fontSize: 14,
    color: fpColors.onSurfaceVariant,
    textAlign: 'center',
    paddingHorizontal: 40,
  },
});
